import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { RxCross2 } from "react-icons/rx";

interface Question {
  text: string;
  category: string;
  complexity: "Daily Questions" | "complex";
}

const questions: Question[] = [
  // Daily Questions Questions
  {
    text: "Show the top 10 most used SWIFT message types in the last 5 years.",
    category: "Volume and Traffic Reports",
    complexity: "Daily Questions",
  },
  {
    text: "Give me the monthly volume of OUTPUT messages over the last year.",
    category: "Volume and Traffic Reports",
    complexity: "Daily Questions",
  },
  {
    text: "Which 10 BICs have sent the most messages in the last five years?",
    category: "Volume and Traffic Reports",
    complexity: "Daily Questions",
  },
  {
    text: "Which 10 transactions had the highest amount in USD last month?",
    category: "Transaction Amount and Currency Analytics",
    complexity: "Daily Questions",
  },
  {
    text: "Give me the average transaction amount per currency in the last 6 months.",
    category: "Transaction Amount and Currency Analytics",
    complexity: "Daily Questions",
  },
  {
    text: "Show 10 recent transactions involving banks in LEBANON.",
    category: "Country-Based Analysis",
    complexity: "Daily Questions",
  },
  {
    text: "Which countries received the highest transaction volume this quarter?",
    category: "Country-Based Analysis",
    complexity: "Daily Questions",
  },
  {
    text: "Show top 10 BICs by total amount sent between 2022 and 2025.",
    category: "BIC and Correspondent Bank Reports",
    complexity: "Daily Questions",
  },
  {
    text: "Give me 10 messages that have no acknowledgment status.",
    category: "Status and Acknowledgement Monitoring",
    complexity: "Daily Questions",
  },
  {
    text: "Show 10 messages containing the word 'NOTPROVIDED' in the message text.",
    category: "Compliance and Risk Triggers",
    complexity: "Daily Questions",
  },
  {
    text: "List 10 messages from the customer 'AEG COMPANY'.",
    category: "Customer Activity",
    complexity: "Daily Questions",
  },
  {
    text: "Show all messages for a customer where ordering customer is empty.",
    category: "Customer Activity",
    complexity: "Daily Questions",
  },
  {
    text: "Which customers made transactions above 1 million in April 2025?",
    category: "Customer Activity",
    complexity: "Daily Questions",
  },
  {
    text: "Show top 10 slowest message acknowledgments this year.",
    category: "Performance and Latency",
    complexity: "Daily Questions",
  },
  {
    text: "Give me 10 INPUT messages from last 2 years.",
    category: "Directional Analysis (INPUT vs OUTPUT)",
    complexity: "Daily Questions",
  },
  {
    text: "List the top 10 OUTPUT messages with the highest value.",
    category: "Directional Analysis (INPUT vs OUTPUT)",
    complexity: "Daily Questions",
  },
  {
    text: "Show 10 messages where Reference contains the word 'invoice'.",
    category: "Keyword or Tag-Based Search",
    complexity: "Daily Questions",
  },
  {
    text: "List 10 messages with missing GPI values.",
    category: "GPI and UETR Tracking",
    complexity: "Daily Questions",
  },
  {
    text: "Give me messages where UETR is not null and status is pending.",
    category: "GPI and UETR Tracking",
    complexity: "Daily Questions",
  },
  {
    text: "List messages created today along with their type and amount.",
    category: "Custom Date-Based Reports",
    complexity: "Daily Questions",
  },
  // Complex Questions
  {
    text: "Can you list customers who never made any transactions involving banks whose messages were rejected within 7 days after their creation?",
    category: "Complex Analysis",
    complexity: "complex",
  },
  {
    text: "Find the top 10 BICs sending the highest total transaction amounts per month over the last 5 years.",
    category: "Complex Analysis",
    complexity: "complex",
  },
  {
    text: "Present a summary view of message counts for each BIC Per Dollar, Euro and British pounds, currencies dynamically shown as columns based on actual message data",
    category: "Complex Analysis",
    complexity: "complex",
  },
  {
    text: "Identify messages sent within overlapping fiscal quarters and calendar months, categorizing each transaction into both fiscal and calendar date-groupings",
    category: "Complex Analysis",
    complexity: "complex",
  },
  {
    text: "Can you provide all messages in Swift format created between May 3, 2021, and May 9, 2025, specifically for the Allied Engineering Group",
    category: "Complex Analysis",
    complexity: "complex",
  },
  {
    text: "I need a report of all distinct SWIFT MT 202 messages created between 24 April 2022 and 12 May 2025 whose Own BIC is BCYPCY2N010, HEBACY2NXXX, ISLDSAJEXXX, or SPXALBBEXXX. Include only messages where either Field_Value or MessageText contains 'T001', and where field_option 20 shows a value that starts with '2022' and also contains '706T where responderdn is null",
    category: "Complex Analysis",
    complexity: "complex",
  },
  {
    text: "List 10 INPUT messages created after January 1, 2020, for the owener BIC codes SPXALBBEXXX or ISLDSAJEXXX, in either MX or SWIFT formats, that either lack an EndToEndID or have 'NOTPROVIDED' explicitly stated, and their acknowledgment session or sequence fields are empty or null",
    category: "Complex Analysis",
    complexity: "complex",
  },
  {
    text: "Find all messages generated between May 3, 2021, and May 9, 2025, using currency EUR, specifically involving OwnBIC of SPXALBBEXXX, where the acknowledgment date is earlier than the creation date, indicating possible timestamp anomalies.",
    category: "Complex Analysis",
    complexity: "complex",
  },
  {
    text: "Retrieve MX messages with SubFormat containing INPUT, created in the last three years, whose message amounts are negative or zero, and whose BizAgrCode field matches 'CBPRPLUS' exactly.",
    category: "Complex Analysis",
    complexity: "complex",
  },
  {
    text: "Provide all SWIFT messages with qualifiers including 'COV' or 'STP', created between 2020 and 2025, for EUR currency, where messages have normal priority yet have not received acknowledgment.",
    category: "Complex Analysis",
    complexity: "complex",
  },
  {
    text: "Show me all distinct Swift INPUT messages of type 202, in EUR, with value date 13 November 2020, sent by BIC SPXALBBEXXX to a correspondent BIC that contains 'BCITITMMXXX', and whose field 57A text includes 'BCI', along with every message detail.",
    category: "Complex Analysis",
    complexity: "complex",
  },
  {
    text: "Find all Swift messages of type 103, in EUR, created between May 4 2020 and May 13 2025, that originate from BIC SPXALBBEXXX, have a partner BIC containing 'ABKLCY2NXXX', use the qualifier 'STP', and have '20220706MT001' anywhere in the Reference. Show all details such as amount, creation and ack dates, ordering and beneficiary customers, message status, and every related ID",
    category: "Complex Analysis",
    complexity: "complex",
  },
  {
    text: "Can you provide all Swift-format INPUT messages of type 103 created between May 1, 2019, and May 9, 2025, for the BIC codes 'ABKLCY2NXXX' and 'SPXALBBEXXX', specifically where the currency is EUR? Include details such as message type, amount, currency, beneficiary customer, ordering customer, message status, and related identifiers",
    category: "Complex Analysis",
    complexity: "complex",
  },
  {
    text: "Provide all MX-format messages created between April 24, 2022, and May 9, 2025, for the BIC code 'SPXALBBEXXX', where the currency is EUR, the reference includes '6f5', and the acknowledgment status is empty. Include details such as message type, amount, currency, beneficiary customer, ordering customer, message status, and related identifiers.",
    category: "Complex Analysis",
    complexity: "complex",
  },
  {
    text: "Can you show all MX-format messages of type pacs.008 created between January 1, 2022, and today, where the beneficiary customer or ordering customer fields contain null values, and the reference fields are completely uppercase?",
    category: "Complex Analysis",
    complexity: "complex",
  },
];

const QuestionsList = ({
  showQuestionsList,
  questionsListActive,
  setInputMessage,
  inputRef,
}: any) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedComplexity, setSelectedComplexity] = useState<
    "all" | "Daily Questions" | "complex"
  >("all");

  useEffect(() => {
    if (questionsListActive) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [questionsListActive]);

  const handleChildClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  const filteredQuestions = questions.filter((question) => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch =
      question.text.toLowerCase().includes(searchLower) ||
      question.category.toLowerCase().includes(searchLower);
    const matchesComplexity =
      selectedComplexity === "all" ||
      question.complexity === selectedComplexity;
    return matchesSearch && matchesComplexity;
  });

  const categories = Array.from(
    new Set(filteredQuestions.map((q) => q.category))
  );

  return (
    <AnimatePresence>
      <motion.div
        onClick={showQuestionsList}
        initial={{ opacity: 0, x: -1 }}
        whileInView={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -1 }}
        viewport={{ once: false }}
        transition={{ duration: 0.7 }}
        className={
          questionsListActive
            ? "fixed inset-0 flex items-center justify-center z-50 backdrop-blur-md"
            : "hidden"
        }
      >
        <div
          className="bg-[#EDEAF8] rounded-lg shadow-2xl"
          onClick={handleChildClick}
        >
          <div className="md:w-[750px] w-[350px]">
            <div className="flex flex-col">
              <div className="relative flex flex-row items-center justify-between p-4">
                <button
                  onClick={showQuestionsList}
                  className="text-[25px] bg-[#4D30B5] hover:bg-[#1b1436] duration-300 rounded-lg p-1 text-white absolute top-2 right-2"
                >
                  <RxCross2 size={25} className="-mt-[1px]" />
                </button>
              </div>
            </div>
            <div className="p-4">
              <div className="flex flex-col">
                <div className="flex flex-col gap-4">
                  <h1 className="text-center text-[25px] font-bold">
                    Prompt Library
                  </h1>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Search by question or category..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="border-[2px] text-black outline-none p-2 rounded-lg flex-1"
                    />
                    <select
                      value={selectedComplexity}
                      onChange={(e) =>
                        setSelectedComplexity(
                          e.target.value as
                            | "all"
                            | "Daily Questions"
                            | "complex"
                        )
                      }
                      className="border-[2px] text-black outline-none p-2 rounded-lg"
                    >
                      <option value="all">All Questions</option>
                      <option value="Daily Questions">Daily Questions</option>
                      <option value="complex">Complex</option>
                    </select>
                  </div>
                  <div className="h-[500px] overflow-auto">
                    {categories.map((category) => (
                      <div key={category} className="mb-6">
                        <h2 className="text-xl font-semibold mb-3 text-[#4D30B5] underline underline-offset-4">
                          {category}
                        </h2>
                        <div className="grid lg:grid-cols-2 gap-4">
                          {filteredQuestions
                            .filter((q) => q.category === category)
                            .map((question, index) => (
                              <div
                                key={index}
                                className="border p-4 rounded-lg cursor-pointer hover:shadow-lg duration-300 relative bg-white"
                                onClick={() => {
                                  setInputMessage(question.text);
                                  showQuestionsList();
                                  inputRef.current?.focus();
                                }}
                              >
                                <p className="text-gray-600">{question.text}</p>
                                {/* <span className="absolute top-2 right-2 text-xs px-2 py-1 rounded-full bg-[#4D30B5] text-white">
                                  {question.complexity}
                                </span> */}
                              </div>
                            ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default QuestionsList;
